import { FiClock, FiMinus, FiPlus, FiTag } from 'react-icons/fi';
import type { EventTicket } from '../../api/events';
import { getTicketSalesStatus } from '../../utils/eventTickets';
import { formatDate, formatTime, formatDateTimeLong } from '../../utils/eventDate';

interface TicketSelectionCardProps {
  ticket: EventTicket;
  quantity: number;
  onAdd: (ticketId: number) => void;
  onRemove: (ticketId: number) => void;
  timezone?: string;
  disabled?: boolean;
}

export function TicketSelectionCard({
  ticket,
  quantity,
  onAdd,
  onRemove,
  timezone,
  disabled,
}: TicketSelectionCardProps) {
  const salesStatus = getTicketSalesStatus(ticket);
  const isSalesBlocked = salesStatus !== 'available';
  const isAddDisabled = disabled || isSalesBlocked || ticket.id === undefined;
  const salesMessage =
    salesStatus === 'not_started' && ticket.start_at
      ? `On sale from ${formatDate(ticket.start_at, timezone, true)} at ${formatTime(ticket.start_at, timezone, true)}`
      : salesStatus === 'ended'
        ? ticket.expired_at
          ? `Sales ended ${formatDateTimeLong(ticket.expired_at, timezone, true)}`
          : 'Ticket sales have ended'
        : null;

  return (
    <div className="flex items-stretch gap-0 bg-zinc-900/60 border border-white/8 border-l-4 border-l-(--event-accent) rounded-2xl overflow-hidden">
      <div className="flex items-start gap-3 p-4 flex-1 min-w-0">
        <div className="w-9 h-9 rounded-full bg-zinc-800 border border-white/10 flex items-center justify-center shrink-0">
          <FiTag className="text-zinc-300" size={15} />
        </div>
        <div className="min-w-0">
          <p className="text-white font-semibold text-[15px]">
            {ticket.ticket_type || 'General Admission'}
          </p>
          <p className="text-[color-mix(in_oklab,var(--event-accent)_75%,white)] font-bold text-[14px] mt-0.5">
            {ticket.price === 0 ? 'Free' : `$${ticket.price.toLocaleString()}`}
          </p>
          {ticket.description && (
            <p className="text-zinc-500 text-[13px] mt-1.5">{ticket.description}</p>
          )}
          {salesMessage && (
            <div className="flex items-center gap-1.5 text-zinc-400 text-[12px] mt-2">
              <FiClock size={12} className="shrink-0" />
              <span>{salesMessage}</span>
            </div>
          )}
        </div>
      </div>

      <div className="hidden sm:block border-l border-dashed border-white/15 my-4" />

      <div className="flex items-center justify-center p-4 shrink-0">
        {quantity > 0 ? (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => ticket.id !== undefined && onRemove(ticket.id)}
              disabled={disabled}
              className="w-8 h-8 rounded-full bg-(--event-accent) text-(--event-accent-fg) flex items-center justify-center hover:brightness-110 active:brightness-95 transition-[filter] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <FiMinus size={13} />
            </button>
            <span className="text-white font-semibold text-[14px] min-w-5 text-center">
              {quantity}
            </span>
            <button
              type="button"
              onClick={() => ticket.id !== undefined && onAdd(ticket.id)}
              disabled={isAddDisabled}
              className="w-8 h-8 rounded-full bg-(--event-accent) text-(--event-accent-fg) flex items-center justify-center hover:brightness-110 active:brightness-95 transition-[filter] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <FiPlus size={13} />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => ticket.id !== undefined && onAdd(ticket.id)}
            disabled={isAddDisabled}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-(--event-accent) text-(--event-accent-fg) hover:brightness-110 active:brightness-95 text-[13px] font-semibold transition-[filter] disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
          >
            <FiPlus size={13} />
            Add Ticket
          </button>
        )}
      </div>
    </div>
  );
}
